import {Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {DxChartComponent, DxChartModule} from "devextreme-angular";
import {VisualRange} from "devextreme-angular/common/charts";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AggregateType, ITrendingQuery, MyScoreClient} from "@services";
import {Subject, forkJoin, of, Observable} from "rxjs";
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, tap} from "rxjs/operators";
import {PercentPipe} from "@angular/common";

type TrendingDataPoint = {
    day: Date,
    val: number
};

@Component({
    selector: 'app-trending',
    imports: [
        DxChartModule
    ],
    templateUrl: './trending.component.html'
})
export class TrendingComponent implements OnInit, OnChanges {
    private myScoreClient = inject(MyScoreClient)
    private destroyRef = inject(DestroyRef);
    pipe = new PercentPipe('en-US');

    @Input() employeeId: string | null = null;
    @Input() formatMode: 'percent' | 'decimal' = 'percent';
    @Input() aggregateType: AggregateType = AggregateType.Day;
    @Input() loadData: (query: ITrendingQuery) => Observable<{ timeSeries?: { [key: string]: number } }>;

    @ViewChild('trendingChart', {static: false}) trendingChart: DxChartComponent;

    private readonly visualRange$ = new Subject<VisualRange>();
    private cachedTimeSeries: { [key: string]: number } = {};
    private loadedRanges: { start: number; end: number }[] = [];
    private _visualRange: VisualRange;

    bounds: {
        startValue: Date,
        endValue: Date
    };

    trendingData: TrendingDataPoint[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['aggregateType']) {
            this.cachedTimeSeries = {};
            this.loadedRanges = [];
            this.trendingData = [];

            this.trendingChart?.instance.getArgumentAxis().visualRange(this.initialVisualRange);
        }
    }

    ngOnInit(): void {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);

        this.bounds = {
            startValue: d,
            endValue: new Date()
        };

        this.visualRange$
            .pipe(
                debounceTime(100),
                map((range) => {
                    const start = new Date(range?.startValue as any);
                    const end = new Date(range?.endValue as any);

                    start.setHours(0, 0, 0, 0);
                    end.setHours(0, 0, 0, 0);

                    return {
                        from: start,
                        to: end,
                        key: `${start.getTime()}-${end.getTime()}`,
                    };
                }),
                distinctUntilChanged((a, b) => a.key === b.key),
                switchMap(({from, to}) => {
                    const missing = this.getMissingRanges(from.getTime(), to.getTime());

                    if (missing.length === 0) {
                        return of(false);
                    }

                    const requests = missing.map(range => {
                        const buffer = this.scrollingWindowDays * 24 * 60 * 60 * 1000;
                        const bufferedStart = range.start - buffer;

                        if (!this.loadData) return of(null);

                        return this.loadData({
                            from: new Date(bufferedStart),
                            to: new Date(range.end),
                            userId: this.employeeId,
                            aggregateType: this.aggregateType
                        }).pipe(
                            tap(trending => {
                                Object.assign(this.cachedTimeSeries, trending.timeSeries ?? {});
                                this.addLoadedRange(bufferedStart, range.end);
                            }),
                            catchError(() => of(null))
                        );
                    });

                    return forkJoin(requests);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((shouldUpdate) => {
                if (shouldUpdate) {
                    this.updateTrendingData();
                }
            });

        this._visualRange = this.initialVisualRange;
        this.visualRange$.next(this._visualRange);
    }

    private updateTrendingData() {
        this.trendingData = Object.entries(this.cachedTimeSeries)
            .map(([key, value]) => ({
                day: new Date(key),
                val: value
            }))
            .sort((a, b) => a.day.getTime() - b.day.getTime());
    }

    private getMissingRanges(reqStart: number, reqEnd: number): { start: number, end: number }[] {
        const missing: { start: number, end: number }[] = [];
        let current = reqStart;
        const oneDay = 24 * 60 * 60 * 1000;

        for (const range of this.loadedRanges) {
            if (range.end < current) continue;
            if (range.start > reqEnd) break;

            if (range.start > current) {
                missing.push({start: current, end: range.start - oneDay});
            }
            current = Math.max(current, range.end + oneDay);
        }

        if (current <= reqEnd) {
            missing.push({start: current, end: reqEnd});
        }

        return missing;
    }

    private addLoadedRange(start: number, end: number) {
        this.loadedRanges.push({start, end});
        this.loadedRanges.sort((a, b) => a.start - b.start);

        const merged: { start: number, end: number }[] = [];
        const oneDay = 24 * 60 * 60 * 1000;

        for (const range of this.loadedRanges) {
            if (merged.length === 0) {
                merged.push(range);
            } else {
                const last = merged[merged.length - 1];
                // Merge overlapping or adjacent ranges
                if (range.start <= last.end + oneDay) {
                    last.end = Math.max(last.end, range.end);
                } else {
                    merged.push(range);
                }
            }
        }
        this.loadedRanges = merged;
    }

    get currentVisualRange(): VisualRange {
        return this._visualRange;
    }

    set currentVisualRange(range: VisualRange) {
        this._visualRange.startValue = range.startValue;
        this._visualRange.endValue = range.endValue;

        this.visualRange$.next(this._visualRange);
    }

    get tickInterval(): "day" | "month" | "week" {
        switch (this.aggregateType) {
            case AggregateType.Day:
                return "day";
            case AggregateType.Week:
                return "week";
            case AggregateType.Month:
                return "month";
        }
    }

    get scrollingWindowDays() {
        switch (this.aggregateType) {
            case AggregateType.Day:
                return 14;
            case AggregateType.Week:
                return 90;
            case AggregateType.Month:
                return 365;
        }
    }

    get initialVisualRange(): VisualRange {
        return {
            startValue: new Date(Date.now() - this.scrollingWindowDays * 24 * 60 * 60 * 1000),
            length: {
                days: this.scrollingWindowDays,
            }
        }
    }

    customizeTooltip = (point: any) => ({
        text: this.formatMode === 'decimal' ? `${point.value}` : `${this.pipe.transform(point.value)}`,
    });
}
